import type { EngagementCandidate } from '@clockwise/shared';
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SHOW_OUT_OF_SCOPE } from '@/lib/out-of-scope';
import { cn } from '@/lib/utils';

interface Props {
  open: boolean;
  /** Named in the subtitle — by the time this opens the user may have looked
   *  away, and "which engagement?" with no subject is unanswerable. */
  customerName: string | undefined;
  activityName: string | undefined;
  /** The engagements that apply. These are the answer set. */
  candidates: readonly EngagementCandidate[];
  /** Live engagements that do NOT cover the activity. Folded away behind a
   *  disclosure unless they are the only thing on offer — they are a rare
   *  deliberate override, and showing them by default doubles the reading. */
  others: readonly EngagementCandidate[];
  /** Currently stored engagement, ticked so re-opening reads as a state. */
  currentEngagementId: string | null;
  onSelect: (engagementId: string) => void;
  onDismiss: () => void;
}

/**
 * Asks which engagement a row's time belongs to.
 *
 * A dialog, not an inline dropdown, because this is RARE and CONSEQUENTIAL: it
 * fires only when two or more engagements cover the same (customer, date,
 * activity), and the answer decides which contract the hours are billed under.
 *
 * The whole design goal is that it costs a second to answer. So it states the
 * question, lists the engagements that apply, and commits on the click that
 * picks one — no confirm step, no explanation of how the app decided, no
 * repetition of terms that are identical on every option. Anything the user
 * does not need in order to choose is either folded away or gone.
 *
 * Cancelling backs out of whatever raised the question: the activity pick
 * that made the entry ambiguous is undone, or the refused save is abandoned.
 * There is no "answer later" — the server refuses to save an entry several
 * engagements cover until one is chosen, so a deferred answer would only
 * resurface as a failed save.
 */
export function TimesheetEngagementDialog({
  open,
  customerName,
  activityName,
  candidates,
  others,
  currentEngagementId,
  onSelect,
  onDismiss,
}: Props) {
  // Others are the whole answer when nothing covers the activity — there is
  // no disclosure to hide behind then.
  const [showOthers, setShowOthers] = useState(candidates.length === 0);

  useEffect(() => {
    if (open) setShowOthers(candidates.length === 0);
  }, [open, candidates.length]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onDismiss();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Which engagement?</DialogTitle>
          {/* The subject, in the one place the eye already is. It replaces both
              a boxed context row and a sentence explaining why we're asking —
              the user does not need the app's reasoning to answer. */}
          <DialogDescription>
            {[customerName, activityName].filter(Boolean).join(' · ')}
          </DialogDescription>
        </DialogHeader>

        <div className="-mx-1 flex max-h-[22rem] flex-col gap-1 overflow-y-auto px-1">
          {candidates.map((e) => (
            <EngagementOption
              key={e.engagementId}
              engagement={e}
              current={e.engagementId === currentEngagementId}
              onPick={onSelect}
            />
          ))}

          {others.length > 0 && !showOthers ? (
            <button
              type="button"
              onClick={() => setShowOthers(true)}
              className="self-start rounded-md px-1 py-1 text-muted-foreground text-xs underline underline-offset-2 hover:text-foreground"
            >
              {others.length === 1
                ? 'Show 1 other engagement'
                : `Show ${others.length} other engagements`}
            </button>
          ) : null}

          {showOthers && others.length > 0 ? (
            <>
              <div className="px-1 pt-1 text-muted-foreground text-xs">
                {candidates.length === 0
                  ? 'None of these lists this activity. You can still pick one.'
                  : 'Other engagements:'}
              </div>
              {others.map((e) => (
                <EngagementOption
                  key={e.engagementId}
                  engagement={e}
                  current={e.engagementId === currentEngagementId}
                  // Only a retainer or metrics engagement has a scope to be
                  // outside of. T&M bills every hour at its role's rate, so a
                  // T&M off its list is simply attributed, never out of scope.
                  // And not labelled at all until the feature is released.
                  outOfScope={SHOW_OUT_OF_SCOPE && e.billingModel !== 'tm'}
                  onPick={onSelect}
                />
              ))}
            </>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One choice. Clicking it IS the answer — the same commit-on-click the
 * customer and activity pickers use, so there is no disabled confirm button to
 * reach for and no second click to spend. A wrong pick is changeable from the
 * engagement line under the customer.
 */
function EngagementOption({
  engagement,
  current,
  outOfScope = false,
  onPick,
}: {
  engagement: EngagementCandidate;
  current: boolean;
  /** Picking it records the time outside the engagement's scope. */
  outOfScope?: boolean;
  onPick: (engagementId: string) => void;
}) {
  // Only what tells one option from another. The start date is on every
  // engagement and "open" is the norm, so neither earns a line; a term that
  // actually ENDS does, because it bounds where the hours can go.
  const detail = [
    engagement.documentNo,
    engagement.effectiveTo ? `ends ${engagement.effectiveTo}` : null,
    outOfScope ? 'out of scope' : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <button
      type="button"
      onClick={() => onPick(engagement.engagementId)}
      className={cn(
        'flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left transition-colors',
        current ? 'border-primary bg-primary/5' : 'hover:border-primary/40 hover:bg-muted/50',
      )}
    >
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-medium text-sm">{engagement.name}</span>
        {detail ? (
          <span className="truncate text-muted-foreground text-xs tabular-nums">{detail}</span>
        ) : null}
      </span>
      {current ? <Check aria-label="Current" className="ml-auto size-4 text-primary" /> : null}
    </button>
  );
}
